import { AppRouteRecordRaw } from '../types';
import { Roles } from '@/constants/roles';
import { BMW_ROUTE_NAMES } from '@/constants/bmw-backoffice';

const Body = () => import('@/components/body.vue');
const BackofficeOverview = () => import('@/pages/backoffice/overview/BackofficeOverview.vue');
const GetFeatures = () => import('@/pages/backoffice/features/GetFeatures.vue');
const SponsorsList = () => import('@/pages/backoffice/Sponsors/index.vue');
const SponsorForm = () => import('@/pages/backoffice/Sponsors/SponsorForm.vue');
const SponsorView = () => import('@/pages/backoffice/Sponsors/SponsorView.vue');
const SponsorBulkUpdateWorkflow = () =>
  import('@/pages/backoffice/sponsor-bulk-update/workflow/SponsorBulkUpdateWorkflow.vue');
const SponsorBulkUpdateJourneyList = () =>
  import('@/pages/backoffice/sponsor-bulk-update/journey-list/SponsorBulkUpdateJourneyList.vue');
const SponsorBulkUpdateJourneyDetail = () =>
  import('@/pages/backoffice/sponsor-bulk-update/journey-detail/SponsorBulkUpdateJourneyDetail.vue');
const AddFeatureConfiguration = () =>
  import('@/pages/backoffice/features/AddFeatureConfiguration/index.vue');
const FeatureConfigurationPage = () =>
  import('@/pages/backoffice/features/FeatureConfigurationPage.vue');
const ListPriceDashboard = () => import('@/pages/backoffice/list-price/ListPriceDashboard.vue');
const PartnerIncomingEmails = () =>
  import('@/pages/backoffice/list-price/incoming-email/PartnerIncomingEmails.vue');
const PartnerIncomingEmailDetail = () =>
  import('@/pages/backoffice/list-price/incoming-email/PartnerIncomingEmailDetail.vue');
const ContactManagement = () =>
  import('@/pages/backoffice/list-price/contact-management/ContactManagement.vue');
const PriceAdjustment = () =>
  import('@/pages/backoffice/list-price/price-adjustment/PriceAdjustment.vue');
const PriceHistory = () => import('@/pages/backoffice/list-price/price-history/PriceHistory.vue');
const PartnerDiscount = () =>
  import('@/pages/backoffice/list-price/partner-discount/PartnerDiscount.vue');
const EmailPreview = () => import('@/pages/backoffice/list-price/email-preview/EmailPreview.vue');
const ApiPartnerDiscountDashboard = () =>
  import('@/pages/backoffice/api-partner-discount/dashboard/ApiPartnerDiscountDashboard.vue');
const ApiPartnerDiscountForm = () =>
  import('@/pages/backoffice/api-partner-discount/form/ApiPartnerDiscountForm.vue');
const MetadataSynchronisation = () =>
  import('@/pages/backoffice/administration/MetadataSynchronisation.vue');
const CloudflareFirebaseRemoteConfig = () =>
  import('@/pages/backoffice/administration/CloudflareFirebaseRemoteConfig.vue');
const NotificationsOverview = () =>
  import('@/pages/backoffice/notifications/overview/NotificationsOverview.vue');
const NotificationTemplates = () =>
  import('@/pages/backoffice/notifications/templates/NotificationTemplates.vue');
const NotificationCampaigns = () =>
  import('@/pages/backoffice/notifications/campaigns/NotificationCampaigns.vue');
const NotificationDispatchMonitor = () =>
  import('@/pages/backoffice/notifications/dispatches/NotificationDispatchMonitor.vue');
const NotificationDeliveryTrace = () =>
  import('@/pages/backoffice/notifications/dispatches/NotificationDeliveryTrace.vue');
const NotificationTestSend = () =>
  import('@/pages/backoffice/notifications/test-send/NotificationTestSend.vue');
const NotificationPushTokens = () =>
  import('@/pages/backoffice/notifications/push-tokens/NotificationPushTokens.vue');
const NotificationsKnowledgeBase = () =>
  import('@/pages/backoffice/notifications/NotificationsKnowledgeBase.vue');
const PromotionCampaigns = () =>
  import('@/pages/backoffice/notifications/promotion-campaigns/PromotionCampaigns.vue');
const TokenManagement = () => import('@/pages/backoffice/administration/TokenManagement.vue');
const AllEmailDomains = () => import('@/pages/backoffice/email-domains/AllEmailDomains.vue');
const AddEmailDomain = () => import('@/pages/backoffice/email-domains/AddEmailDomain.vue');
const EditEmailDomain = () => import('@/pages/backoffice/email-domains/EditEmailDomain.vue');
const OrderLookupAuditLogs = () =>
  import('@/pages/backoffice/order-lookup/audit-logs/OrderLookupAuditLogs.vue');
const OrderLookupAuditAnalytics = () =>
  import('@/pages/backoffice/order-lookup/analytics/OrderLookupAuditAnalytics.vue');
const ConnectTrackingInternal = () =>
  import('@/pages/backoffice/connect-tracking/ConnectTrackingInternal.vue');
const PartnerFilterRulesOverview = () =>
  import('@/pages/backoffice/partner-filter-rules/overview/PartnerFilterRulesOverview.vue');
const PartnerFilterPolicyList = () =>
  import('@/pages/backoffice/partner-filter-rules/policy-list/PartnerFilterPolicyList.vue');
const PartnerFilterPolicyDetail = () =>
  import('@/pages/backoffice/partner-filter-rules/policy-detail/PartnerFilterPolicyDetail.vue');
const RestrictedEntityList = () =>
  import('@/pages/backoffice/partner-filter-rules/restricted-entity-list/RestrictedEntityList.vue');
const RestrictedEntityDetail = () =>
  import('@/pages/backoffice/partner-filter-rules/restricted-entity-detail/RestrictedEntityDetail.vue');
const PredefinedPartnerRules = () =>
  import('@/pages/backoffice/partner-filter-rules/predefined/PredefinedPartnerRules.vue');
const PartnerFilterDecisionCheck = () =>
  import('@/pages/backoffice/partner-filter-rules/decision-check/DecisionCheck.vue');
const PartnerPricingOverview = () =>
  import('@/pages/backoffice/partner-pricing/overview/PartnerPricingOverview.vue');
const PartnerPricingLookup = () =>
  import('@/pages/backoffice/partner-pricing/lookup/PartnerPricingLookup.vue');
const BmwHealthCheck = () => import('@/pages/backoffice/bmw/health-check/BmwHealthCheck.vue');
const BmwHowItWorks = () => import('@/pages/backoffice/bmw/how-it-works/BmwHowItWorks.vue');
const BmwPagesOverview = () => import('@/pages/backoffice/bmw/pages-overview/BmwPagesOverview.vue');

export const backofficeRoutes: AppRouteRecordRaw[] = [
  {
    path: '/backoffice',
    component: Body,
    redirect: '/backoffice/overview',
    meta: {
      breadcrumb: 'Internal Backoffice',
    },
    children: [
      {
        path: 'overview',
        name: 'BackofficeOverview',
        component: BackofficeOverview,
        meta: {
          title: 'Internal Backoffice Overview | ryd Portal',
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Overview',
        },
      },
      // BMW: the BMW fleet integration; more pages follow (constants/bmw-backoffice.ts)
      {
        path: 'bmw',
        redirect: { name: BMW_ROUTE_NAMES.healthCheck },
        meta: {
          requiredUserGroup: ['ryd'],
          breadcrumb: 'BMW',
        },
        children: [
          // The page was called Health Check; keep old bookmarks and links working.
          { path: 'health-check', redirect: { name: BMW_ROUTE_NAMES.healthCheck } },
          {
            path: 'readiness-check',
            name: BMW_ROUTE_NAMES.healthCheck,
            component: BmwHealthCheck,
            meta: {
              title: 'BMW Readiness Check | ryd Portal',
              requiredUserGroup: ['ryd'],
              breadcrumb: 'Readiness Check',
            },
          },
          {
            path: 'how-it-works',
            name: BMW_ROUTE_NAMES.howItWorks,
            component: BmwHowItWorks,
            meta: {
              title: 'BMW How It Works | ryd Portal',
              requiredUserGroup: ['ryd'],
              breadcrumb: 'How It Works',
            },
          },
          {
            path: 'pages',
            name: BMW_ROUTE_NAMES.pagesOverview,
            component: BmwPagesOverview,
            meta: {
              title: 'BMW Pages & Access | ryd Portal',
              requiredUserGroup: ['ryd'],
              breadcrumb: 'Pages & Access',
            },
          },
        ],
      },
      // Sponsors
      {
        path: 'sponsors',
        name: 'sponsorsRoot',
        redirect: { name: 'sponsorsList' },
        meta: {
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Sponsors',
        },
        children: [
          {
            path: '',
            name: 'sponsorsList',
            component: SponsorsList,
            meta: {
              title: 'Sponsors | ryd Portal',
              headTitle: 'Sponsor Management',
            },
          },
          {
            path: 'create',
            name: 'sponsorCreate',
            component: SponsorForm,
            meta: {
              title: 'Create Sponsor | ryd Portal',
              headTitle: 'Create Sponsor',
              requiredRoles: ['sponsors_admin'],
              breadcrumb: 'Create',
            },
          },
          {
            path: ':id/edit',
            name: 'sponsorEdit',
            component: SponsorForm,
            meta: {
              title: 'Edit Sponsor | ryd Portal',
              headTitle: 'Edit Sponsor',
              requiredRoles: ['sponsors_admin'],
              breadcrumb: 'Edit',
            },
          },
          {
            path: ':id',
            name: 'sponsorView',
            component: SponsorView,
            meta: {
              title: 'View Sponsor | ryd Portal',
              headTitle: 'View Sponsor',
              breadcrumb: 'View',
            },
          },
        ],
      },
      // Sponsor Bulk Updates (batch processing)
      {
        path: 'sponsor-bulk-update',
        name: 'sponsorBulkUpdateWorkflow',
        component: SponsorBulkUpdateWorkflow,
        meta: {
          title: 'Bulk Update Sponsors | ryd Portal',
          headTitle: 'Bulk Update Sponsors',
          requiredUserGroup: ['ryd'],
          requiredRoles: ['sponsors_admin'],
          breadcrumb: 'Bulk Update Sponsors',
        },
      },
      {
        path: 'sponsor-bulk-update/journeys',
        name: 'sponsorBulkUpdateJourneys',
        component: SponsorBulkUpdateJourneyList,
        meta: {
          title: 'Sponsor Bulk Update Journeys | ryd Portal',
          headTitle: 'Sponsor Bulk Update Journeys',
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Bulk Update Journeys',
        },
      },
      {
        path: 'sponsor-bulk-update/journeys/:journeyId',
        name: 'sponsorBulkUpdateJourney',
        component: SponsorBulkUpdateJourneyDetail,
        meta: {
          title: 'Sponsor Bulk Update Journey | ryd Portal',
          headTitle: 'Sponsor Bulk Update Journey',
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Bulk Update Journey',
        },
      },
      {
        path: 'sponsor-bulk-update/new',
        redirect: { name: 'sponsorBulkUpdateWorkflow' },
      },
      {
        path: 'sponsor-bulk-update/:runId',
        redirect: (to) => ({
          name: 'sponsorBulkUpdateJourney',
          params: { journeyId: to.params.runId },
          query: to.query,
        }),
      },
      // Mailchimp Audience Export
      {
        path: 'mailchimp-audience-export',
        redirect: '/ryd-marketing/mailchimp-audience-export',
      },
      // Features
      {
        path: 'features',
        name: 'featuresRoot',
        meta: {
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Features',
        },
        redirect: { name: 'featuresList' },
        children: [
          {
            path: '',
            name: 'featuresList',
            component: GetFeatures,
            meta: {
              title: 'Features | ryd Portal',
              headTitle: 'Feature Management',
            },
          },
          {
            path: 'view',
            name: 'featureConfigView',
            component: FeatureConfigurationPage,
            meta: {
              title: 'Feature Configuration | ryd Portal',
              headTitle: 'Feature Configuration',
              breadcrumb: 'Configuration',
            },
          },
          {
            path: 'edit',
            name: 'featureConfigEdit',
            component: FeatureConfigurationPage,
            meta: {
              requiredRoles: ['operations_admin'],
              title: 'Edit Feature Configuration | ryd Portal',
              headTitle: 'Edit Feature Configuration',
              breadcrumb: 'Edit Configuration',
            },
          },
          {
            path: 'clone',
            name: 'featureConfigClone',
            component: FeatureConfigurationPage,
            meta: {
              requiredRoles: ['operations_admin'],
              title: 'Clone Feature Configuration | ryd Portal',
              headTitle: 'Clone Feature Configuration',
              breadcrumb: 'Clone Configuration',
            },
          },
          {
            path: 'add/:id',
            name: 'featureConfigAdd',
            component: AddFeatureConfiguration,
            meta: {
              requiredRoles: ['operations_admin'],
              title: 'Add Feature Configuration | ryd Portal',
              headTitle: 'Add Feature Configuration',
              breadcrumb: 'Add Configuration',
            },
          },
          {
            path: 'add',
            // redirect from old url
            redirect: { name: 'featuresList' },
          },
        ],
      },
      {
        path: 'fleet/card-manager',
        name: 'FleetCardManager',
        redirect: '/fleet-operations/fleet-card-manager',
      },
      {
        path: 'fleet/card-creator',
        name: 'FleetCardCreator',
        redirect: '/fleet-operations/fleet-card-creator',
      },
      {
        path: 'fleet/partner-list',
        name: 'FleetPartnerListBackoffice',
        redirect: '/fleet-operations/fleet-partner-overview',
      },
      {
        path: 'list-price',
        name: 'ListPriceDashboard',
        component: ListPriceDashboard,
        meta: {
          title: 'List Price Management | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'List Price Management',
        },
      },
      {
        path: 'list-price/partner-incoming-emails',
        name: 'PartnerIncomingEmails',
        component: PartnerIncomingEmails,
        meta: {
          title: 'Partner Incoming Emails | ryd Portal',
          breadcrumb: 'Partner Incoming Emails',
        },
      },
      {
        path: 'list-price/partner-incoming-emails/:id',
        name: 'PartnerIncomingEmailDetail',
        component: PartnerIncomingEmailDetail,
        meta: {
          title: 'Partner Incoming Email Detail | ryd Portal',
          breadcrumb: 'Partner Incoming Email Detail',
        },
      },
      {
        path: 'list-price/contact-management',
        name: 'ListPriceContactManagement',
        component: ContactManagement,
        meta: {
          title: 'List Price Contact Management | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Contact Management',
        },
      },
      {
        path: 'list-price/price-adjustment',
        name: 'PriceAdjustment',
        component: PriceAdjustment,
        meta: {
          title: 'Price Adjustments | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Price Adjustments',
        },
      },
      {
        path: 'list-price/price-history',
        name: 'PriceHistory',
        component: PriceHistory,
        meta: {
          title: 'Price History | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Price History',
        },
      },
      {
        path: 'list-price/partner-discount',
        name: 'PartnerDiscount',
        component: PartnerDiscount,
        meta: {
          title: 'Partner Discounts | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Partner Discounts',
        },
      },
      {
        path: 'list-price/email-preview',
        name: 'EmailPreview',
        component: EmailPreview,
        meta: {
          title: 'Email Preview & Test Send | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Email Preview & Test Send',
        },
      },
      {
        path: 'api-partner-discount',
        name: 'ApiPartnerDiscountDashboard',
        component: ApiPartnerDiscountDashboard,
        meta: {
          title: 'API Partner Discounts | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'API Partner Discounts',
        },
      },
      {
        path: 'api-partner-discount/new',
        name: 'ApiPartnerDiscountCreate',
        component: ApiPartnerDiscountForm,
        meta: {
          title: 'New API Partner Discount | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'New API Partner Discount',
        },
      },
      {
        path: 'api-partner-discount/:id/edit',
        name: 'ApiPartnerDiscountEdit',
        component: ApiPartnerDiscountForm,
        meta: {
          title: 'Edit API Partner Discount | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Edit API Partner Discount',
        },
      },
      {
        path: 'administration/metadata-sync',
        name: 'InternalOperationsMetadataSync',
        component: MetadataSynchronisation,
        meta: {
          title: 'Metadata Sync | ryd Portal',
          breadcrumb: 'Administration',
        },
      },
      {
        path: 'administration/cloudflare-firebase-remote-config',
        name: 'CloudflareFirebaseRemoteConfig',
        component: CloudflareFirebaseRemoteConfig,
        meta: {
          title: 'Cloudflare WAF & Firebase Remote Config | ryd Portal',
          breadcrumb: 'Administration',
          requiresAuth: true,
        },
      },
      {
        path: 'administration/token-management',
        name: 'TokenManagement',
        component: TokenManagement,
        meta: {
          title: 'Token Management | ryd Portal',
          breadcrumb: 'Administration',
          requiresAuth: true,
        },
      },
      {
        // Now Internal Backoffice → AI Assistants → Support Chat Agent Whitelist (ai-assistants.routes.ts).
        // Kept as a redirect because this URL was shared with operations and is bookmarked.
        path: 'administration/support-chat-agent-whitelist',
        redirect: { name: 'SupportChatAgentWhitelist' },
      },
      {
        path: 'email-domains',
        name: 'AllEmailDomains',
        component: AllEmailDomains,
        meta: {
          title: 'Email Domain Management',
          breadcrumb: 'Domain Management',
        },
      },
      {
        path: 'email-domains/add',
        name: 'AddEmailDomain',
        component: AddEmailDomain,
        meta: {
          title: 'Add Email Domain',
          breadcrumb: 'Add Domain',
        },
      },
      {
        path: 'email-domains/edit/:id',
        name: 'EditEmailDomain',
        component: EditEmailDomain,
        meta: {
          title: 'Edit Email Domain',
          breadcrumb: 'Edit Domain',
        },
      },
      {
        path: 'order-lookup/audit-logs',
        name: 'OrderLookupAuditLogs',
        component: OrderLookupAuditLogs,
        meta: {
          title: 'Order Lookup Audit Logs | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Order Lookup Audit Logs',
        },
      },
      {
        path: 'order-lookup/analytics',
        name: 'OrderLookupAuditAnalytics',
        component: OrderLookupAuditAnalytics,
        meta: {
          title: 'Order Lookup Analytics | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Order Lookup Analytics',
        },
      },
      {
        path: 'connect-tracking',
        name: 'ConnectTrackingInternal',
        component: ConnectTrackingInternal,
        meta: {
          title: 'ryd Connect Tracking | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'ryd Connect Tracking',
        },
      },

      // Partner Filter Rules — station visibility policies and the
      // restricted-entity registry. The route gate is read-only access; write
      // capability is re-checked per request in rp-users, so route visibility
      // never implies mutation rights.
      {
        path: 'partner-filter-rules',
        name: 'partnerFilterRulesRoot',
        redirect: { name: 'partnerFilterRulesOverview' },
        meta: {
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Partner Filter Rules',
        },
        children: [
          {
            path: '',
            name: 'partnerFilterRulesOverview',
            component: PartnerFilterRulesOverview,
            meta: {
              title: 'Partner Filter Rules | ryd Portal',
              headTitle: 'Partner Filter Rules',
            },
          },
          {
            path: 'policies',
            name: 'partnerFilterPolicyList',
            component: PartnerFilterPolicyList,
            meta: {
              title: 'Station Visibility Policies | ryd Portal',
              headTitle: 'Station Visibility Policies',
              breadcrumb: 'Policies',
            },
          },
          {
            path: 'policies/:id',
            name: 'partnerFilterPolicyDetail',
            component: PartnerFilterPolicyDetail,
            meta: {
              title: 'Policy Details | ryd Portal',
              headTitle: 'Policy Details',
              breadcrumb: 'Policy',
            },
          },
          {
            path: 'restricted-entities',
            name: 'partnerFilterRestrictedEntityList',
            component: RestrictedEntityList,
            meta: {
              title: 'Restricted-Entity Registry | ryd Portal',
              headTitle: 'Restricted-Entity Registry',
              breadcrumb: 'Restricted Entities',
            },
          },
          {
            path: 'restricted-entities/:id',
            name: 'partnerFilterRestrictedEntityDetail',
            component: RestrictedEntityDetail,
            meta: {
              title: 'Restricted Entity | ryd Portal',
              headTitle: 'Restricted Entity',
              breadcrumb: 'Entity',
            },
          },
          {
            path: 'predefined',
            name: 'partnerFilterPredefinedRules',
            component: PredefinedPartnerRules,
            meta: {
              title: 'Predefined Partner Rules | ryd Portal',
              headTitle: 'Predefined Partner Rules',
              breadcrumb: 'Predefined Rules',
            },
          },
          {
            path: 'decision-check',
            name: 'partnerFilterDecisionCheck',
            component: PartnerFilterDecisionCheck,
            meta: {
              title: 'Decision Check | ryd Portal',
              headTitle: 'Decision Check',
              breadcrumb: 'Decision Check',
            },
          },
        ],
      },
      // Partner Pricing — read-only lookup over the List Pricing Engine and the
      // API Partner Discount Engine (TASKFORCE-2733). Nothing here mutates, so the
      // ryd user group is the whole access rule; rp-users enforces the same.
      {
        path: 'partner-pricing',
        name: 'partnerPricingRoot',
        redirect: { name: 'partnerPricingOverview' },
        meta: {
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Partner Pricing',
        },
        children: [
          {
            path: '',
            name: 'partnerPricingOverview',
            component: PartnerPricingOverview,
            meta: {
              title: 'Partner Pricing | ryd Portal',
              headTitle: 'Partner Pricing',
            },
          },
          {
            path: 'lookup',
            name: 'partnerPricingLookup',
            component: PartnerPricingLookup,
            meta: {
              title: 'Partner Pricing Lookup | ryd Portal',
              headTitle: 'Partner Pricing Lookup',
              breadcrumb: 'Pricing Lookup',
            },
          },
        ],
      },
      // Notifications — notification-dispatch-service management.
      // Role enforcement lives in rp-users, which re-checks it on every request; these routes
      // only gate on the ryd user group, matching the rest of this section. A user without the
      // role sees the page and an authorisation error from the API rather than a 404.
      {
        // Bare /backoffice/notifications should land on the overview rather than 404.
        // No `name` here: naming a redirect-only record makes Vue Router warn.
        path: 'notifications',
        redirect: { name: 'notifications-overview' },
      },
      {
        path: 'notifications/overview',
        name: 'notifications-overview',
        component: NotificationsOverview,
        meta: {
          title: 'Notifications | ryd Portal',
          headTitle: 'Notifications',
          breadcrumb: 'Notifications',
        },
      },
      {
        path: 'notifications/templates',
        name: 'notification-templates',
        component: NotificationTemplates,
        meta: {
          title: 'Notification Templates | ryd Portal',
          headTitle: 'Notification Templates',
          breadcrumb: 'Notification Templates',
        },
      },
      {
        path: 'notifications/campaigns',
        name: 'notification-campaigns',
        component: NotificationCampaigns,
        meta: {
          title: 'Notification Campaigns | ryd Portal',
          headTitle: 'Notification Campaigns',
          breadcrumb: 'Notification Campaigns',
        },
      },
      {
        path: 'notifications/promotion-campaigns',
        name: 'promotion-campaigns',
        component: PromotionCampaigns,
        meta: {
          title: 'Promotion Campaigns | ryd Portal',
          headTitle: 'Promotion Campaigns',
          breadcrumb: 'Promotion Campaigns',
        },
      },
      {
        path: 'notifications/dispatches',
        name: 'notification-dispatches',
        component: NotificationDispatchMonitor,
        meta: {
          title: 'Dispatch Monitor | ryd Portal',
          headTitle: 'Dispatch Monitor',
          breadcrumb: 'Dispatch Monitor',
        },
      },
      {
        path: 'notifications/dispatches/:id/trace',
        name: 'notification-dispatch-trace',
        component: NotificationDeliveryTrace,
        meta: {
          title: 'Delivery Trace | ryd Portal',
          headTitle: 'Delivery Trace',
          breadcrumb: 'Delivery Trace',
        },
      },
      {
        path: 'notifications/test-send',
        name: 'notification-test-send',
        component: NotificationTestSend,
        meta: {
          title: 'Notification Test Send | ryd Portal',
          headTitle: 'Notification Test Send',
          breadcrumb: 'Test Send',
        },
      },
      {
        path: 'notifications/push-tokens',
        name: 'notification-push-tokens',
        component: NotificationPushTokens,
        meta: {
          title: 'Push Tokens | ryd Portal',
          headTitle: 'Push Tokens',
          breadcrumb: 'Push Tokens',
        },
      },
      {
        path: 'notifications/knowledge-base',
        name: 'notifications-knowledge-base',
        component: NotificationsKnowledgeBase,
        meta: {
          title: 'Notifications Knowledge Base | ryd Portal',
          headTitle: 'Notifications Knowledge Base',
          breadcrumb: 'Notifications Knowledge Base',
        },
      },
    ],
  },
];
